import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Typography } from '@mui/material';

import { people } from '../tagging/tagging';
import { countNow, describeManageError } from '../manage/manage';
import { DeleteDialog } from '../manage/ManageParts';
import { ManagedTag } from './tagManager';
import { CRM_DELETE_TAG } from './tags.queries';

interface DeleteTagDialogProps {
  tag: ManagedTag;
  onClose: () => void;
  onDeleted: () => void;
}

/**
 * Deletes a tag, confirmed against how many carry it. The API deletes nothing
 * unless that number is still right; when it is not, the new number is shown
 * and confirmed again. A tag on anyone needs its name typed: its history leaves
 * every timeline it was on, and nothing brings it back.
 */
export const DeleteTagDialog = ({ tag, onClose, onDeleted }: DeleteTagDialogProps) => {
  const [confirmed, setConfirmed] = useState(tag.contactCount);
  const [refusal, setRefusal] = useState<unknown>();
  const [remove, { loading }] = useMutation(CRM_DELETE_TAG);

  const run = async () => {
    setRefusal(undefined);
    try {
      await remove({ variables: { id: tag.id, expectedCount: confirmed } });
      onDeleted();
    } catch (err) {
      const now = countNow(err);
      if (now !== undefined) setConfirmed(now);
      setRefusal(err);
    }
  };

  return (
    <DeleteDialog
      title={`Delete ${tag.name}?`}
      confirm={confirmed ? `Delete and untag ${people(confirmed)}` : 'Delete tag'}
      typeToConfirm={confirmed ? tag.name : undefined}
      error={refusal === undefined ? undefined : describeManageError(refusal, 'tag')}
      running={loading}
      onConfirm={run}
      onClose={onClose}
    >
      {confirmed ? (
        <>
          <Typography variant="body2">
            It is taken off <strong>{people(confirmed)}</strong>, and it leaves every filter and saved link that used it.
          </Typography>
          <Typography variant="body2">
            Its history goes too: when it was added to and removed from each person leaves their timeline. To keep
            that, rename the tag instead.
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
            This cannot be undone.
          </Typography>
        </>
      ) : (
        <Typography variant="body2">
          Nobody carries this tag, so no contact changes. It leaves the tag list and the tag pickers.
        </Typography>
      )}
    </DeleteDialog>
  );
};
